/**
 * The sign-in view. In production one CTA leads to Entra; in development (auth_mode=local) the
 * quick sign-in buttons for the seeded users appear instead of a broken Microsoft round trip.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth";
import { AccentBlock, Button, Card } from "../components/primitives";
import { useToast } from "../components/toast";

const DEV_USERS = [
  { email: "johan.lemstrom@nordiskabrand.se", label: "Johan Lemström (admin)" },
  { email: "kollega@nordiskabrand.se", label: "Anna Karlsson (medlem)" },
];

export function LoginPage() {
  const { auth, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showToast = useToast();
  const [authMode, setAuthMode] = useState<string | null>(null);

  const errorMessage = searchParams.get("fel");

  useEffect(() => {
    fetch("/api/v1/meta")
      .then((response) => response.json())
      .then((meta: { auth_mode: string }) => setAuthMode(meta.auth_mode))
      .catch(() => setAuthMode(null));
  }, []);

  // Already signed in? This page has nothing to offer.
  useEffect(() => {
    if (auth.status === "signed_in") {
      navigate("/", { replace: true });
    }
  }, [auth.status, navigate]);

  const devLogin = async (email: string) => {
    const response = await fetch("/api/v1/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (response.ok) {
      await refresh();
      navigate("/", { replace: true });
    } else {
      showToast({ message: "Utvecklingsinloggningen misslyckades.", tone: "fail" });
    }
  };

  return (
    <div className="mx-auto grid w-[min(440px,100%)] gap-s6 pt-[8vh]">
      <Card hero>
        <p className="nb-overline">Nordiska Brand AB</p>
        <h1 className="nb-title mt-s4 text-h2 text-ink">Logga in i Noteshelf</h1>
        <p className="mt-s4 text-body text-muted">
          Använd ditt vanliga Microsoft-konto. Inga separata lösenord.
        </p>

        {errorMessage && (
          <AccentBlock className="mt-s5" style={{ borderLeftColor: "var(--fail-text)" }}>
            <p role="alert" className="text-body text-ink">
              {errorMessage}
            </p>
          </AccentBlock>
        )}

        <div className="mt-s6">
          {/* Full page navigation on purpose: the OIDC round trip is a redirect dance. */}
          <Button variant="cta" onClick={() => window.location.assign("/auth/login")}>
            Logga in med Microsoft
          </Button>
        </div>
      </Card>

      {authMode === "local" && (
        <Card sunken>
          <h2 className="nb-overline">Utvecklingsläge</h2>
          <p className="mt-s4 text-small text-muted">
            Snabbinloggning som seedad användare. Finns aldrig i produktion.
          </p>
          <div className="mt-s5 flex flex-wrap gap-s4">
            {DEV_USERS.map((devUser) => (
              <Button key={devUser.email} onClick={() => void devLogin(devUser.email)}>
                {devUser.label}
              </Button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
